import { request } from '@/services/apiClient';
import type {
	MfaAction,
	MfaStatus,
	ReauthenticateInput,
	ReauthResult,
	RecoveryCodesResult,
	TotpEnrollment,
	VerifyMfaInput,
	VerifyResult,
} from '../types';

/**
 * MFA management endpoints. Tokens returned here (proof, enrollment,
 * recovery codes) are credentials: never log, cache, or send them anywhere
 * other than the backend that issued them.
 */
export const mfaApi = {
	/**
	 * Complete a login or reauthentication challenge. The login challenge
	 * carries no access token, and business 401s (`MFA_CODE_INVALID`, …) must
	 * not be mistaken for an expired session, hence `skipAuthRefresh`.
	 */
	verifyMfa(input: VerifyMfaInput): Promise<VerifyResult> {
		return request<VerifyResult>({
			url: '/v1/auth/mfa/verify',
			method: 'POST',
			data: input,
			skipAuthRefresh: true,
		});
	},

	status(): Promise<MfaStatus> {
		return request<MfaStatus>({ url: '/v1/auth/mfa/status', method: 'GET' });
	},

	/**
	 * Prove identity for a sensitive MFA action. A wrong password returns a
	 * business 401 (`REAUTHENTICATION_REQUIRED`), so the session is never
	 * refreshed or cleared from this call.
	 */
	reauthenticate(input: ReauthenticateInput): Promise<ReauthResult> {
		return request<ReauthResult>({
			url: '/v1/auth/reauthenticate',
			method: 'POST',
			data: input,
			skipAuthRefresh: true,
		});
	},

	/** Begin TOTP enrollment (or replace an existing factor). */
	setup(
		action: Extract<MfaAction, 'mfa_setup' | 'mfa_replace'>,
		proofToken: string,
	) {
		return request<TotpEnrollment>({
			url: '/v1/auth/mfa/totp/setup',
			method: 'POST',
			data: { action, proofToken },
		});
	},

	/** Confirm the first TOTP code and receive one-time recovery codes. */
	confirm(enrollmentToken: string, code: string) {
		return request<RecoveryCodesResult>({
			url: '/v1/auth/mfa/totp/confirm',
			method: 'POST',
			data: { enrollmentToken, code },
		});
	},

	/** Disable TOTP. Returns `204 No Content`; sessions are revoked server-side. */
	disable(proofToken: string) {
		return request<void>({
			url: '/v1/auth/mfa/totp',
			method: 'DELETE',
			data: { proofToken },
			skipAuthRefresh: true,
		});
	},

	/** Issue a fresh set of recovery codes, invalidating the previous set. */
	regenerate(proofToken: string) {
		return request<RecoveryCodesResult>({
			url: '/v1/auth/mfa/recovery-codes/regenerate',
			method: 'POST',
			data: { proofToken },
			skipAuthRefresh: true,
		});
	},
};
